import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import CheckCircleIcon from '@atlaskit/icon/core/migration/success--editor-success';
import { ModalFooter as ModalFooterAtlaskit } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Box, xcss } from '@atlaskit/primitives';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
// Disabling this until isGICPositioningEnabled ff is cleaned up
import { useIsGICPositioningEnabled } from '../../../controllers/gic-positioning-enabled/main.tsx';
// Disabling this until isGICPositioningEnabled ff is cleaned up
import { ISSUE_CREATE_FORM } from '../../constants/selectors.tsx';
import { getIssueDiscoveryUrl } from '../../utils/issue-discovery/index.tsx';
import { GIC_VARIANTS } from './constants.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const PassThroughWrapper = ({ children }: { children: ReactNode; content?: ReactNode }) => (
	<>{children}</>
);

const ModalFooter = (props: { children: ReactNode; isMiniVariant: boolean }) => {
	const { isMiniVariant, children } = props;
	if (isMiniVariant) {
		/**
		 * We need to use our own footer and not the atlaskit one since the atlaskit one
		 * needs to be within ModalDialog to get context or else it crashes
		 */

		return (
			<Box xcss={miniFooterStyles} testId="issue-create.common.ui.footer.footer-mini">
				{children}
			</Box>
		);
	}
	return <ModalFooterAtlaskit>{children}</ModalFooterAtlaskit>;
};

export const Footer = (props: Props) => {
	const { formatMessage } = useIntl();
	const {
		onClose,
		onSubmit,
		isSubmitting,
		isOperationInProgress = false,
		onCreateAnotherChange,
		canCreateAnotherIssue,
		shouldDisableSubmit = false,
		createdIssues,
		variant = GIC_VARIANTS.DEFAULT,
		displayCreateAnotherIssueToggle: newDisplayCreateAnotherIssueToggle,
		showIssuesCreated,
		showCancelButton,
	} = props;
	const numberOfIssuesCreated = createdIssues.length;

	let displayCreateAnotherIssueToggle = newDisplayCreateAnotherIssueToggle;
	const isGICPositioningEnabled = useIsGICPositioningEnabled();

	const isMiniVariant = isGICPositioningEnabled() && variant === GIC_VARIANTS.MINI;

	if (!isGICPositioningEnabled()) {
		const [{ displayCreateAnotherIssueToggle: oldDisplayCreateAnotherIssueToggle }] =
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useTriggerIssueCreateModal();
		displayCreateAnotherIssueToggle = oldDisplayCreateAnotherIssueToggle;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onViewIssuesLinkClicked = (_: any, analyticsEvent: UIAnalyticsEvent): void => {
		fireUIAnalytics(analyticsEvent, 'viewIssue');
	};

	const getPrimaryButtonCopy = () => {
		if (isMiniVariant) {
			return messages.createButtonInMiniView;
		}
		return messages.createButton;
	};

	const IssuesCreatedTooltip = isMiniVariant ? Tooltip : PassThroughWrapper;
	const showIssueCreatedTooltip = Boolean(numberOfIssuesCreated) && Boolean(showIssuesCreated);

	const cancelBtn = props?.dismissModalEntryPointWrapper ? (
		<props.dismissModalEntryPointWrapper>
			<Button
				appearance="subtle-link"
				onClick={onClose}
				isDisabled={isSubmitting}
				testId="issue-create.common.ui.footer.cancel-button"
				interactionName="global-issue.create-modal.cancel"
			>
				{formatMessage(messages.cancelButton)}
			</Button>
		</props.dismissModalEntryPointWrapper>
	) : (
		<Button
			appearance="subtle-link"
			onClick={onClose}
			isDisabled={isSubmitting}
			testId="issue-create.common.ui.footer.cancel-button"
			interactionName="global-issue.create-modal.cancel"
		>
			{formatMessage(messages.cancelButton)}
		</Button>
	);

	return (
		<ModalFooter isMiniVariant={isMiniVariant}>
			{displayCreateAnotherIssueToggle && (
				<Checkbox
					onChange={onCreateAnotherChange}
					label={formatMessage(messages.createAnother)}
					isChecked={canCreateAnotherIssue}
					isDisabled={shouldDisableSubmit}
					testId="issue-create.common.ui.footer.checkbox"
				/>
			)}
			{showIssueCreatedTooltip && (
				<IssuesCreatedTooltip
					content={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.issuesCreatedLinkIssueTermRefresh
							: messages.issuesCreatedLink,
						{
							issueCount: numberOfIssuesCreated,
						},
					)}
				>
					<IssueLinkContainer>
						<CheckCircleIcon
							label=""
							LEGACY_size="medium"
							spacing="spacious"
							color={token('color.icon.success')}
						/>
						<Button
							appearance="subtle-link"
							spacing="none"
							href={getIssueDiscoveryUrl(createdIssues)}
							target="_blank"
							onClick={onViewIssuesLinkClicked}
							testId="issue-create.common.ui.footer.created-issue-link"
							interactionName="global-issue.create-modal.created-issue-link"
						>
							{isMiniVariant
								? numberOfIssuesCreated
								: formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.issuesCreatedLinkIssueTermRefresh
											: messages.issuesCreatedLink,
										{
											issueCount: numberOfIssuesCreated,
										},
									)}
						</Button>
					</IssueLinkContainer>
				</IssuesCreatedTooltip>
			)}
			{(!isMiniVariant || showCancelButton) && (
				<CloseButtonContainer>{cancelBtn}</CloseButtonContainer>
			)}
			<LoadingButton
				appearance="primary"
				onClick={onSubmit}
				isLoading={isSubmitting || isOperationInProgress}
				isDisabled={isSubmitting || shouldDisableSubmit}
				type="submit"
				form={ISSUE_CREATE_FORM}
				testId="issue-create.common.ui.footer.create-button"
				interactionName="global-issue.create-modal.submit"
			>
				{isGICPositioningEnabled()
					? formatMessage(getPrimaryButtonCopy())
					: formatMessage(messages.createButton)}
			</LoadingButton>
		</ModalFooter>
	);
};

export default Footer;

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { GIC_VARIANTS } from './constants';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonContainer = styled.div<{
	children?: ReactNode;
}>({
	marginLeft: 'auto',
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueLinkContainer = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	alignItems: 'center',
	marginLeft: token('space.200'),
	marginRight: 'auto',
});

const miniFooterStyles = xcss({
	display: 'flex',
	position: 'relative',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 'space.050',
	flex: '0 0 auto',
	marginTop: 'space.0',
	marginBottom: 'space.0',
	marginLeft: 'space.150',
	marginRight: 'space.150',
	paddingTop: 'space.250',
	paddingBottom: 'space.250',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	borderTop: `0.5px solid ${token('color.border')}`,
	boxShadow: 'elevation.shadow.overflow.spread',
});
